/**
 * VideoReviewPage.tsx
 * ─────────────────────────────────────────────────────────────
 * 관리자 전용 — 영상 검수 페이지
 *
 * 흐름:
 *  1. pending 영상 목록 표시
 *  2. AI 1차 검토 결과 표시 (시뮬레이션)
 *  3. 관리자 최종 승인 / 반려
 */

import React, { useState } from 'react'
import {
  CheckCircle, XCircle, Clock, Eye, AlertTriangle,
  Youtube, ChevronDown, ChevronUp, Bot, User,
  Star, Award, Loader2, RefreshCw, Filter
} from 'lucide-react'
import {
  useVideoStore, ConceptVideo, VideoStatus,
  CREDIT_PER_VIEW, getYouTubeEmbedUrl,
} from '../store/videoStore'

// ── AI 검토 시뮬레이션 ─────────────────────────────────────────
interface AiReview {
  score: number          // 0~100
  passed: boolean
  checks: { label: string; ok: boolean; note: string }[]
  summary: string
}

function simulateAiReview(video: ConceptVideo): AiReview {
  // 실제 환경에서는 LLM API 호출
  const hasYoutube = !!video.youtubeUrl
  const titleOk    = video.title.length >= 5 && video.title.length <= 60
  const descOk     = video.description.length >= 20
  const durationOk = video.durationSec <= 360   // 6분 이하 (5분 + 여유)
  const tagsOk     = video.tags.length >= 1

  const checks = [
    { label: '제목 적합성',  ok: titleOk,    note: titleOk    ? '제목 길이 및 형식 정상' : '제목이 너무 짧거나 깁니다 (5~60자)' },
    { label: '설명 충분성',  ok: descOk,     note: descOk     ? '설명이 충분히 작성됨'   : '설명이 너무 짧습니다 (20자 이상)' },
    { label: '영상 길이',    ok: durationOk, note: durationOk ? '5분 이내 조건 충족'     : '5분 초과 — 반려 대상' },
    { label: '영상 링크',    ok: hasYoutube, note: hasYoutube ? 'YouTube URL 확인됨'     : '영상 링크 없음' },
    { label: '태그 입력',    ok: tagsOk,     note: tagsOk     ? '태그 정상 등록'         : '태그를 1개 이상 입력해주세요' },
  ]

  const passed = checks.filter(c => c.ok).length >= 4
  const score  = Math.round((checks.filter(c => c.ok).length / checks.length) * 100)

  return {
    score,
    passed,
    checks,
    summary: passed
      ? '기본 조건을 충족합니다. 관리자 최종 확인 후 승인해주세요.'
      : '일부 조건을 충족하지 못했습니다. 반려 사유를 상세히 안내해주세요.',
  }
}

// ── 상태 배지 ─────────────────────────────────────────────────
function StatusBadge({ status }: { status: VideoStatus }) {
  const map = {
    pending:  { label: '검수 대기', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
    approved: { label: '승인됨',    cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    rejected: { label: '반려됨',    cls: 'bg-red-100 text-red-700 border-red-200' },
  }
  const { label, cls } = map[status]
  return (
    <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${cls}`}>{label}</span>
  )
}

// ── 개별 영상 검수 카드 ───────────────────────────────────────
function ReviewCard({ video }: { video: ConceptVideo }) {
  const [open, setOpen]           = useState(false)
  const [aiReview, setAiReview]   = useState<AiReview | null>(null)
  const [aiLoading, setAiLoading] = useState(false)
  const [rejectNote, setRejectNote] = useState('')
  const [showRejectBox, setShowRejectBox] = useState(false)
  const [acting, setActing]       = useState(false)

  const { approveVideo, rejectVideo } = useVideoStore(s => ({
    approveVideo: s.approveVideo,
    rejectVideo:  s.rejectVideo,
  }))

  const runAiReview = () => {
    setAiLoading(true)
    setTimeout(() => {
      setAiReview(simulateAiReview(video))
      setAiLoading(false)
    }, 1400)
  }

  const handleApprove = () => {
    setActing(true)
    setTimeout(() => {
      approveVideo(video.id, '관리자 승인')
      setActing(false)
    }, 600)
  }

  const handleReject = () => {
    if (!rejectNote.trim()) return
    setActing(true)
    setTimeout(() => {
      rejectVideo(video.id, rejectNote)
      setActing(false)
      setShowRejectBox(false)
    }, 600)
  }

  const embedUrl = getYouTubeEmbedUrl(video.youtubeUrl ?? '')

  return (
    <div className={`rounded-2xl border-2 overflow-hidden transition-all ${
      video.status === 'approved' ? 'border-emerald-200 bg-emerald-50/30'
      : video.status === 'rejected' ? 'border-red-200 bg-red-50/30'
      : 'border-amber-200 bg-amber-50/30'
    }`}>
      {/* 헤더 */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full text-left p-4 flex items-start gap-3 hover:bg-white/40 transition-colors"
      >
        {/* 썸네일 */}
        <img
          src={video.thumbnailUrl}
          alt={video.title}
          className="w-20 h-14 object-cover rounded-xl flex-shrink-0"
          onError={e => { (e.target as HTMLImageElement).src = 'https://placehold.co/80x56/e2e8f0/94a3b8?text=No+Img' }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <StatusBadge status={video.status} />
            <span className="text-xs text-slate-400 font-mono">{video.conceptId}</span>
            <span className="text-xs text-slate-400">{video.conceptLabel}</span>
          </div>
          <p className="text-sm font-bold text-slate-800 line-clamp-1">{video.title}</p>
          <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
            <span className="flex items-center gap-1"><User size={10} /> {video.creatorName}</span>
            <span className="flex items-center gap-1"><Clock size={10} /> {Math.floor(video.durationSec/60)}분</span>
            <span className="flex items-center gap-1"><Eye size={10} /> {video.viewCount} 시청</span>
            <span>{new Date(video.submittedAt).toLocaleDateString('ko-KR')}</span>
          </div>
        </div>
        {open
          ? <ChevronUp size={16} className="text-slate-400 flex-shrink-0 mt-1" />
          : <ChevronDown size={16} className="text-slate-400 flex-shrink-0 mt-1" />}
      </button>

      {/* 펼쳐진 내용 */}
      {open && (
        <div className="border-t border-white/60 p-4 bg-white space-y-4">

          {/* 영상 미리보기 */}
          {embedUrl && (
            <div className="rounded-xl overflow-hidden aspect-video bg-black">
              <iframe
                src={embedUrl}
                className="w-full h-full"
                allowFullScreen
                title={video.title}
              />
            </div>
          )}

          {/* 영상 상세 정보 */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            {[
              ['크리에이터', video.creatorName],
              ['개념 ID', video.conceptId],
              ['영상 길이', `${Math.floor(video.durationSec/60)}분 ${video.durationSec%60}초`],
              ['업로드 방식', video.source === 'youtube' ? 'YouTube' : '직접 업로드'],
            ].map(([label, val]) => (
              <div key={label} className="bg-slate-50 rounded-xl p-3">
                <p className="text-xs text-slate-400 mb-0.5">{label}</p>
                <p className="font-semibold text-slate-700">{val}</p>
              </div>
            ))}
          </div>

          {/* 설명 & 태그 */}
          <div className="bg-slate-50 rounded-xl p-3">
            <p className="text-xs font-bold text-slate-500 mb-1">설명</p>
            <p className="text-sm text-slate-700">{video.description}</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {video.tags.map(t => (
                <span key={t} className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">#{t}</span>
              ))}
            </div>
          </div>

          {/* YouTube URL */}
          {video.youtubeUrl && (
            <div className="flex items-center gap-2 text-xs">
              <Youtube size={12} className="text-red-500" />
              <a href={video.youtubeUrl} target="_blank" rel="noopener noreferrer"
                className="text-blue-500 hover:underline truncate">{video.youtubeUrl}</a>
            </div>
          )}

          {/* ── AI 검토 ──────────────────────────────────────── */}
          {video.status === 'pending' && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-black text-blue-700 flex items-center gap-1.5">
                  <Bot size={13} /> AI 1차 검토
                </p>
                {!aiReview && (
                  <button
                    onClick={runAiReview}
                    disabled={aiLoading}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-blue-500 text-white rounded-full font-bold hover:bg-blue-600 transition-colors disabled:opacity-60"
                  >
                    {aiLoading
                      ? <><Loader2 size={11} className="animate-spin" /> 분석 중...</>
                      : <><Bot size={11} /> AI 검토 실행</>}
                  </button>
                )}
              </div>

              {aiReview ? (
                <div>
                  {/* 점수 */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-black text-lg shadow-md ${
                      aiReview.passed ? 'bg-emerald-500' : 'bg-red-500'
                    }`}>
                      {aiReview.score}
                    </div>
                    <div>
                      <p className={`text-sm font-black ${aiReview.passed ? 'text-emerald-700' : 'text-red-700'}`}>
                        {aiReview.passed ? '✅ 통과' : '❌ 미통과'}
                      </p>
                      <p className="text-xs text-slate-600">{aiReview.summary}</p>
                    </div>
                  </div>
                  {/* 체크 항목 */}
                  <div className="space-y-1.5">
                    {aiReview.checks.map(c => (
                      <div key={c.label} className="flex items-center gap-2 text-xs">
                        {c.ok
                          ? <CheckCircle size={12} className="text-emerald-500 flex-shrink-0" />
                          : <XCircle    size={12} className="text-red-400 flex-shrink-0" />}
                        <span className="font-semibold text-slate-600 w-20">{c.label}</span>
                        <span className="text-slate-400">{c.note}</span>
                      </div>
                    ))}
                  </div>
                  {/* 재실행 */}
                  <button onClick={() => { setAiReview(null); runAiReview() }}
                    className="mt-3 flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700">
                    <RefreshCw size={10} /> 재검토
                  </button>
                </div>
              ) : (
                <p className="text-xs text-blue-600">
                  AI 검토를 실행하면 제목·설명·길이·링크 등 기본 조건을 자동으로 확인합니다.
                </p>
              )}
            </div>
          )}

          {/* 관리자 메모 (기존) */}
          {video.adminNote && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
              <p className="text-xs font-bold text-slate-500 mb-1">관리자 메모</p>
              <p className="text-sm text-slate-700">{video.adminNote}</p>
            </div>
          )}

          {/* ── 액션 버튼 (pending만) ─────────────────────── */}
          {video.status === 'pending' && (
            <div className="flex flex-col gap-2">
              {/* 승인 */}
              <button
                onClick={handleApprove}
                disabled={acting}
                className="w-full py-2.5 rounded-xl bg-emerald-500 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-emerald-600 transition-colors disabled:opacity-60"
              >
                {acting
                  ? <Loader2 size={14} className="animate-spin" />
                  : <CheckCircle size={14} />}
                승인 — 즉시 게시
              </button>

              {/* 반려 */}
              {!showRejectBox ? (
                <button
                  onClick={() => setShowRejectBox(true)}
                  className="w-full py-2.5 rounded-xl border-2 border-red-200 text-red-600 font-bold text-sm flex items-center justify-center gap-2 hover:bg-red-50 transition-colors"
                >
                  <XCircle size={14} /> 반려 처리
                </button>
              ) : (
                <div className="border-2 border-red-200 rounded-xl p-3 space-y-2">
                  <label className="text-xs font-bold text-red-600">반려 사유 * (크리에이터에게 이메일 발송)</label>
                  <textarea
                    value={rejectNote}
                    onChange={e => setRejectNote(e.target.value)}
                    placeholder="예: 영상 길이가 5분을 초과합니다. 5분 이내로 편집 후 재신청해주세요."
                    rows={3}
                    className="w-full border border-red-200 rounded-lg px-3 py-2 text-sm outline-none resize-none focus:border-red-400"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowRejectBox(false)}
                      className="flex-1 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50"
                    >
                      취소
                    </button>
                    <button
                      onClick={handleReject}
                      disabled={!rejectNote.trim() || acting}
                      className="flex-1 py-2 rounded-lg bg-red-500 text-white text-sm font-bold hover:bg-red-600 disabled:opacity-50 flex items-center justify-center gap-1"
                    >
                      {acting ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />}
                      반려 확정
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 승인/반려 완료 표시 */}
          {video.status === 'approved' && (
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl p-3">
              <CheckCircle size={16} className="text-emerald-500" />
              <div>
                <p className="text-sm font-bold text-emerald-700">승인됨</p>
                {video.approvedAt && (
                  <p className="text-xs text-emerald-500">{new Date(video.approvedAt).toLocaleString('ko-KR')}</p>
                )}
              </div>
              <div className="ml-auto text-right">
                <p className="text-xs text-emerald-600 font-semibold">총 {video.viewCount} 시청</p>
                <p className="text-xs text-emerald-500">유효 {video.validViewCount} × {CREDIT_PER_VIEW}원</p>
              </div>
            </div>
          )}
          {video.status === 'rejected' && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <p className="text-sm font-bold text-red-700 flex items-center gap-1.5 mb-1">
                <XCircle size={14} /> 반려됨
              </p>
              {video.adminNote && <p className="text-xs text-red-600">{video.adminNote}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// 메인 페이지
// ─────────────────────────────────────────────────────────────
export default function VideoReviewPage() {
  const getAllVideos = useVideoStore(s => s.getAllVideos)
  const videos = getAllVideos()

  const [filter, setFilter] = useState<'all' | VideoStatus>('pending')

  const filtered = filter === 'all' ? videos : videos.filter(v => v.status === filter)

  const counts = {
    all:      videos.length,
    pending:  videos.filter(v => v.status === 'pending').length,
    approved: videos.filter(v => v.status === 'approved').length,
    rejected: videos.filter(v => v.status === 'rejected').length,
  }

  const totalValidViews = videos
    .filter(v => v.status === 'approved')
    .reduce((s, v) => s + v.validViewCount, 0)
  const totalCreditIssued = totalValidViews * CREDIT_PER_VIEW

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-black text-slate-800 flex items-center gap-2 mb-1">
          <Bot size={22} className="text-blue-500" /> 영상 검수 관리
        </h1>
        <p className="text-sm text-slate-500">크리에이터가 등록한 개념 영상을 검토하고 승인/반려합니다.</p>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: '총 영상',     value: counts.all,      color: 'text-slate-700', bg: 'bg-slate-50 border-slate-200' },
          { label: '검수 대기',   value: counts.pending,  color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' },
          { label: '승인됨',      value: counts.approved, color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
          { label: '반려됨',      value: counts.rejected, color: 'text-red-700',   bg: 'bg-red-50 border-red-200' },
        ].map(item => (
          <div key={item.label} className={`rounded-2xl border-2 ${item.bg} p-4 text-center`}>
            <p className={`text-2xl font-black ${item.color}`}>{item.value}</p>
            <p className="text-xs text-slate-500 mt-0.5">{item.label}</p>
          </div>
        ))}
      </div>

      {/* 크레딧 발행 현황 */}
      <div className="bg-gradient-to-r from-violet-50 to-blue-50 border border-violet-200 rounded-2xl p-4 mb-6 flex items-center gap-4">
        <Award size={24} className="text-violet-500 flex-shrink-0" />
        <div>
          <p className="text-xs text-slate-500">총 크레딧 발행액</p>
          <p className="text-xl font-black text-violet-700">
            {totalCreditIssued.toLocaleString()}원
          </p>
        </div>
        <div className="ml-auto text-right">
          <p className="text-xs text-slate-500">유효 시청</p>
          <p className="text-lg font-black text-blue-700">{totalValidViews.toLocaleString()}회</p>
        </div>
      </div>

      {/* 필터 */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {([
          ['all',      `전체 (${counts.all})`],
          ['pending',  `대기 (${counts.pending})`],
          ['approved', `승인 (${counts.approved})`],
          ['rejected', `반려 (${counts.rejected})`],
        ] as const).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setFilter(id)}
            className={`text-xs px-4 py-1.5 rounded-full font-semibold border transition-all ${
              filter === id
                ? 'bg-blue-500 text-white border-blue-500'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* 영상 목록 */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Filter size={32} className="mx-auto mb-3 opacity-40" />
          <p className="font-semibold">해당 상태의 영상이 없습니다</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(v => <ReviewCard key={v.id} video={v} />)}
        </div>
      )}
    </div>
  )
}
